import CustomGoogleMap from 'design-system/atoms/custom-google-map';
import {
  Box,
  Button,
  Flex,
  HStack,
  Tag,
  useTheme,
  VStack,
} from '@chakra-ui/react';
import { FormattedMessage, useIntl } from 'react-intl';
import { InfoWindow, Marker } from '@react-google-maps/api';
import { useRef, useState } from 'react';
import { PublicBusRoute } from '@webapp/bff/src/types/public-transport';
import { wktToPointArray } from '@lib/gis';
import LayoutMultiPanel from '../LayoutMultiPanel';
import Spinner from '../Spinner';
import { SegmentBusRoute } from '../../data/useSearchPublicBusData';
import AnimatedRoute from '../AnimatedRoute';
import { getBusRouteStopIconUrl } from '../SitePublicTransport/BusRouteStopIcon';

type StopsSelection = {
  firstStopId?: string;
  lastStopId?: string;
};
const colorBlue = '#4299E1';
const iconBoxSize = 46;
type BusRouteStop = SegmentBusRoute['stops'][number];
const busIconUrls = {
  waypointFaded: getBusRouteStopIconUrl({
    stopType: 'waypoint',
    boxSize: iconBoxSize,
    color: '#CCCCCC',
  }),
  waypoint: getBusRouteStopIconUrl({
    stopType: 'waypoint',
    boxSize: iconBoxSize,
    color: colorBlue,
  }),
  origin: getBusRouteStopIconUrl({
    stopType: 'origin',
    boxSize: iconBoxSize,
    color: '#67DC95',
  }),
  destination: getBusRouteStopIconUrl({
    stopType: 'destination',
    boxSize: iconBoxSize,
    color: '#67DC95',
  }),
};
export interface BusStopsRouteEditorProps {
  busRoute: SegmentBusRoute;
  onRouteSave: (busRoute: SegmentBusRoute) => void;
  onCancel: () => void;
  isLoading?: boolean;
  isSaving?: boolean;
}

export default function BusStopsRouteEditor({
  busRoute,
  onRouteSave,
  onCancel,
  isLoading,
  isSaving,
}: BusStopsRouteEditorProps) {
  const { formatMessage } = useIntl();
  const [selectedStops, setSelectedStops] = useState<StopsSelection>({
    firstStopId: busRoute.firstStopId,
    lastStopId: busRoute.lastStopId,
  });
  const firstStop = selectedStops.firstStopId
    ? busRoute.stops.find((stop) => stop.stopId === selectedStops.firstStopId)
    : undefined;
  const lastStop = selectedStops.lastStopId
    ? busRoute.stops.find((stop) => stop.stopId === selectedStops.lastStopId)
    : undefined;
  const [initialMapBounds] = useState(
    [firstStop, lastStop]
      .filter((s) => s !== undefined)
      .map((stop) => ({
        lat: stop.lat,
        lng: stop.lon,
      }))
  );
  return (
    <LayoutMultiPanel
      contentSidebar={
        <Flex
          direction="column"
          width="100%"
          height="100%"
          alignItems="stretch"
          justifyContent="space-between"
          padding={2}
        >
          <VStack width="100%" spacing={8} alignItems="stretch">
            <VStack spacing={4} alignItems="flex-start">
              <Box fontSize="lg" fontWeight="bold">
                <FormattedMessage
                  defaultMessage="Edit the start and end or monitoring for this route"
                  id="5HiPKH"
                />
              </Box>
              <Box fontSize="xs">
                <FormattedMessage
                  defaultMessage="Select a stop on the map to change the route start or end"
                  id="QKvVI4"
                />
              </Box>
            </VStack>
            <VStack spacing={4} alignItems="flex-start">
              <Box fontSize="xs" fontWeight="bold">
                {busRoute.routeName}
              </Box>
              <HStack spacing={2}>
                <Tag
                  width="46px"
                  justifyContent="center"
                  variant="solid"
                  size="sm"
                  colorScheme="greenDark"
                >
                  <FormattedMessage defaultMessage="Start" id="mOFG3K" />
                </Tag>
                <Box fontSize="sm">
                  {firstStop?.stopName ??
                    formatMessage({
                      defaultMessage: 'Select the first stop',
                      id: 'YQNeZ7',
                    })}
                </Box>
              </HStack>
              <HStack spacing={2}>
                <Tag
                  variant="solid"
                  justifyContent="center"
                  width="46px"
                  size="sm"
                  colorScheme="greenDark"
                >
                  <FormattedMessage defaultMessage="End" id="3JVa6k" />
                </Tag>
                <Box fontSize="sm">
                  {lastStop?.stopName ??
                    formatMessage({
                      defaultMessage: 'Select the last stop',
                      id: 'T/byho',
                    })}
                </Box>
              </HStack>
            </VStack>
          </VStack>
          <HStack>
            <Button
              isDisabled={
                !selectedStops.firstStopId || !selectedStops.lastStopId
              }
              isLoading={isSaving}
              size="sm"
              colorScheme="greenDark"
              onClick={() => {
                if (selectedStops.firstStopId && selectedStops.lastStopId) {
                  onRouteSave({
                    ...busRoute,
                    firstStopId: selectedStops.firstStopId,
                    lastStopId: selectedStops.lastStopId,
                  });
                }
              }}
            >
              <FormattedMessage defaultMessage="Update route" id="ZKNAJj" />
            </Button>
            <Button
              size="sm"
              colorScheme="greenDark"
              variant="outline"
              onClick={onCancel}
            >
              <FormattedMessage defaultMessage="Cancel" id="47FYwb" />
            </Button>
          </HStack>
        </Flex>
      }
      contentMain={
        <Box width="100%" height="100%" position="relative">
          {isLoading && (
            <Box
              width="100%"
              height="100%"
              position="absolute"
              top={0}
              left={0}
              backgroundColor="whiteAlpha.700"
              zIndex={1}
            >
              <Box
                position="absolute"
                top="50%"
                left="50%"
                transform="translate(-50%, -50%)"
              >
                <Spinner size="lg" />
              </Box>
            </Box>
          )}
          <CustomGoogleMap
            enableScrollZoom
            setBoundsToElements={initialMapBounds}
          >
            <MapLayerBusStopsSelection
              busRoute={busRoute}
              selectedStops={selectedStops}
              setSelectedStops={setSelectedStops}
            />
          </CustomGoogleMap>
        </Box>
      }
      layoutSettings={{
        addPadding: true,
        showLogo: false,
      }}
    />
  );
}
function BusStopMarkers({
  busRoute,
  selectedStops,
  setActiveMarker,
}: {
  busRoute: PublicBusRoute;
  selectedStops: StopsSelection;
  setActiveMarker: (stop: BusRouteStop | null) => void;
}) {
  const firstStopIndex = !selectedStops.firstStopId
    ? -1
    : busRoute.stops.findIndex(
        (stop) => stop.stopId === selectedStops.firstStopId
      );
  const lastStopIndex = !selectedStops.lastStopId
    ? -1
    : busRoute.stops.findIndex(
        (stop) => stop.stopId === selectedStops.lastStopId
      );
  const possibleStops = busRoute.stops.slice(
    firstStopIndex + 1,
    lastStopIndex === -1 ? undefined : lastStopIndex
  );
  return busRoute.stops.map((stop) => {
    const stopStateProps = getStopState({
      stop,
      selectedStops,
      possibleStops,
    });
    return (
      <Marker
        key={stop.stopId}
        position={{ lat: stop.lat, lng: stop.lon }}
        title={stop.stopName}
        onClick={() => {
          setActiveMarker(stop);
        }}
        zIndex={stopStateProps.zIndex}
        icon={{
          url: stopStateProps.iconUrl,
          anchor: new google.maps.Point(iconBoxSize / 2, iconBoxSize / 2),
        }}
      />
    );
  });
}
function MapLayerBusStopsSelection({
  busRoute,
  selectedStops,
  setSelectedStops,
}: {
  busRoute: SegmentBusRoute;
  selectedStops: StopsSelection;
  setSelectedStops: (selectedStops: StopsSelection) => void;
}) {
  const [activeMarker, setActiveMarker] = useState<BusRouteStop | null>();
  const isActiveSelectedFirstStop =
    selectedStops.firstStopId === activeMarker?.stopId;
  const isActiveSelectedLastStop =
    selectedStops.lastStopId === activeMarker?.stopId;
  const isActiveBeforeFirstStop =
    selectedStops.firstStopId !== undefined &&
    busRoute.stops.findIndex(
      (stop) => stop.stopId === selectedStops.firstStopId
    ) >=
      busRoute.stops.findIndex((stop) => stop.stopId === activeMarker?.stopId);
  const isActiveAfterLastStop =
    selectedStops.lastStopId !== undefined &&
    busRoute.stops.findIndex(
      (stop) => stop.stopId === selectedStops.lastStopId
    ) <=
      busRoute.stops.findIndex((stop) => stop.stopId === activeMarker?.stopId);

  const { fonts } = useTheme();
  const headerElement = useRef<HTMLDivElement>(document.createElement('div'));
  headerElement.current.style.fontFamily = fonts.body;
  headerElement.current.style.fontSize = '16px';
  headerElement.current.style.fontWeight = 'bold';
  headerElement.current.innerHTML = activeMarker?.stopName ?? '';
  return (
    <>
      <AnimatedRoute
        routePoints={wktToPointArray(busRoute.wkt)}
        routeColor={colorBlue}
      />
      {activeMarker && (
        <InfoWindow
          position={{ lat: activeMarker.lat, lng: activeMarker.lon }}
          onCloseClick={() => setActiveMarker(null)}
          options={{
            pixelOffset: new google.maps.Size(0, -20),
            minWidth: 200,
            headerContent: headerElement.current,
          }}
        >
          <HStack>
            <Button
              size="sm"
              colorScheme="greenDark"
              variant="solid"
              isDisabled={isActiveSelectedFirstStop || isActiveAfterLastStop}
              onClick={() =>
                setSelectedStops({
                  ...selectedStops,
                  firstStopId: activeMarker.stopId,
                })
              }
            >
              <FormattedMessage defaultMessage="Set as start" id="M+3Uav" />
            </Button>
            <Button
              size="sm"
              colorScheme="greenDark"
              variant="solid"
              isDisabled={isActiveSelectedLastStop || isActiveBeforeFirstStop}
              onClick={() =>
                setSelectedStops({
                  ...selectedStops,
                  lastStopId: activeMarker.stopId,
                })
              }
            >
              <FormattedMessage defaultMessage="Set as end" id="enSEEt" />
            </Button>
          </HStack>
        </InfoWindow>
      )}
      <BusStopMarkers
        busRoute={busRoute}
        selectedStops={selectedStops}
        setActiveMarker={setActiveMarker}
      />
    </>
  );
}

function getStopState({
  stop,
  selectedStops,
  possibleStops,
}: {
  stop: BusRouteStop;
  selectedStops: StopsSelection;
  possibleStops: Pick<BusRouteStop, 'stopId'>[];
}) {
  if (!selectedStops.firstStopId && !selectedStops.lastStopId) {
    return {
      iconUrl: busIconUrls.waypoint,
      zIndex: 0,
    };
  }
  if (
    stop.stopId === selectedStops.firstStopId ||
    stop.stopId === selectedStops.lastStopId
  ) {
    return {
      iconUrl:
        stop.stopId === selectedStops.firstStopId
          ? busIconUrls.origin
          : busIconUrls.destination,
      zIndex: 2,
    };
  }
  if (possibleStops.some((s) => s.stopId === stop.stopId)) {
    return {
      iconUrl: busIconUrls.waypoint,
      zIndex: 1,
    };
  }
  return {
    iconUrl: busIconUrls.waypointFaded,
    zIndex: 0,
  };
}
